import SaveIcon from "@mui/icons-material/Save";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled, Theme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import LoadingButton from "_shared/_views/loading/loading-button";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import axios, { HttpStatusCode } from "axios";
import { populateIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import MarketplaceTeam from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { NavigateFunction } from "react-router-dom";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { 
  createTeamTemplate, 
  getTeamTemplateById, 
  registerTeamTemplateToMarketplace, 
  updateTeamTemplate 
} from "work/team-templates/_store/team-templates-redux-slice";
import TeamTemplate from "work/team-templates/team-template";
import EditVendorTeam from "work/work-agreements/teams/vendor-team/_views/vendor-team-edit-view";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import VendorTeamName from "work/work-agreements/teams/vendor-team/vendor-team-name/vendor-team-name";

const Wrapper = styled("main")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gridTemplateRows: "auto 1fr auto",
  paddingTop: theme.spacing(1),
  width: "100%",
}));
const HeadSection = styled("section")(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    gap: theme.spacing(1),
  },
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  gridColumnStart: 1,
  gridRowStart: 1,
  paddingTop: 0,
  width: "100%",
}));
const HeadInputFields = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  width: "100%",
}));
const TeamNameField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginRight: 0,
    width: "100%",
  },
  marginBottom: 0,
  marginRight: theme.spacing(1),
  width: "100%",
}));
const TeamRoster = styled(EditVendorTeam)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));
const SaveButton = styled(LoadingButton)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    width: "100%",
  },
  width: "fit-content",
}));

type TeamTemplateFormProps = {
  templateId?: Guid;
  navigate: NavigateFunction;
  onFormDirty: (dirty?: boolean) => void;
  onSave?: (team: TeamTemplate) => void;
};

export default function TeamTemplateForm(props: Readonly<TeamTemplateFormProps>) {
  const { 
    templateId, 
    navigate, 
    onFormDirty, 
    onSave
  } = props;

  const teamTemplate = useSelector(getTeamTemplateById(templateId));

  const [formTeamName, setFormTeamName] = React.useState<string>(teamTemplate?.name?.value ?? "");
  const [formSelectedTeamLeader, setFormSelectedTeamLeader] = React.useState<EntityMember | null>(teamTemplate?.leader ?? null);
  const [formSelectedTeamMembers, setFormSelectedTeamMembers] = React.useState<EntityMember[]>(teamTemplate?.members ?? []);
  const [formSelectedMarketplaceId, setFormSelectedMarketplaceId] = React.useState<Guid | undefined>(teamTemplate?.marketplaceId);
  const [formRegisterToMarketplace, setFormRegisterToMarketplace] = React.useState(teamTemplate?.marketplaceId !== undefined);

  const [saving, setSaving] = React.useState(false);
  const [formValidated, setFormValidated] = React.useState(false);

  const session = useSession();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();

  const isLargeDownDisplay = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));


  /*
   * NOTE: In the future, it might be a good idea to implement a "getTeamTemplate" thunk in the team-templates-redux-slice
   * that requests a single team template from the server when we don't find it already in the store. We should also wire 
   * up loading/error states from the store and show loading feedback in the form while it's being fetched to eliminate 
   * the currently existing delay before the team roster component is populated when the form is shown immediately on load. 
   * This also would provide the opportunity to show an error to the user when the team template is not found OR have the 
   * server return 404 and respond to error. All this combined would allow the team-template-form to function as a standalone 
   * component that can be used anywhere in the app without the need for a parent component to pre-populate the team template 
   * into the store.
  useEffect(() => {
    if (teamTemplate || !templateId) return;

    dispatch(
      getTeamTemplate({
        session,
        templateId: templateId
      })
    ).unwrap().catch(() => {
      enqueueSnackbar("Team template could not be loaded.", { variant: "error" });
    });
  }, []);
  */


  useEffect(() => {
    function validateForm() {
      setFormValidated(formSelectedTeamLeader !== undefined && formTeamName !== "");
    }    
    validateForm();
  }, [formSelectedTeamLeader, formSelectedTeamMembers, formSelectedMarketplaceId, formTeamName]);


  useEffect(() => {
    setFormTeamName(teamTemplate?.name?.value ?? "");
    setFormSelectedTeamLeader(teamTemplate?.leader ?? null);
    setFormSelectedTeamMembers(teamTemplate?.members ?? []);
  }, [teamTemplate]);


  function handleLeaderReplaced(leader: EntityMember | null) {
    setFormSelectedTeamLeader(leader);
    onFormDirty(true);
  }

  function handleMemberAdded(member: EntityMember) {
    const selectedTeamMembers = [...formSelectedTeamMembers, member];
    setFormSelectedTeamMembers(selectedTeamMembers);

    dispatch(
      populateIndividualsByUserIds({
        userIds: selectedTeamMembers.map((m) => m.userId)
      })
    );

    onFormDirty(true);
  }

  function handleMemberRemoved(userId?: Guid) {
    if (userId) {
      setFormSelectedTeamMembers(formSelectedTeamMembers.filter((m) => !m.userId?.isEqualTo(userId)));
    } else {
      setFormSelectedTeamMembers([]);
    }
    onFormDirty(true);
  }

  async function handleMarketplaceTeamSelected(team: MarketplaceTeam) {
    if (formSelectedTeamLeader !== null || formSelectedTeamMembers.length > 0) {
      const response = await confirm({
        title: "Replace Team",
        message: "Are you sure you want to replace the current vendor-team?",
        okButtonText: "Replace",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    if (!team.leader.profile?.firstName || !team.leader.profile?.lastName) {
      throw new Error("Team leader profile is undefined");
    }

    setFormSelectedMarketplaceId(team.id);
    setFormSelectedTeamLeader(team.leader.toEntityMember());
    setFormSelectedTeamMembers(team.members.map((member) => member.toEntityMember()));
    setFormTeamName(team.profile.name);

    onFormDirty(true);
  }

  async function handleSaveTeamClick() {
    if (!formSelectedTeamLeader?.userId || !formSelectedTeamLeader.legalEntityId) {
      return;
    }

    try {
      setSaving(true);

      const newTeam = new VendorTeam({
        leader: formSelectedTeamLeader,
        members: formSelectedTeamMembers,
        marketplaceId: formSelectedMarketplaceId,
      });

      let returnedTemplate: TeamTemplate;
      if (teamTemplate?.id) {
        const response = 
          await dispatch(
            updateTeamTemplate({ 
              session, 
              originalTemplate: teamTemplate, 
              updatedTeam: newTeam, 
              updatedTeamName: new VendorTeamName(formTeamName) 
            })
          ).unwrap();
        returnedTemplate = TeamTemplate.fromJSON(response);
      } 
      else {
        const response = 
          await dispatch(
            createTeamTemplate({ 
              session, 
              entityId: session.user!.legalEntityId, 
              team: newTeam, 
              teamName: new VendorTeamName(formTeamName),
              creatorType: session.accountType
            })
          ).unwrap();
        returnedTemplate = TeamTemplate.fromJSON(response);
      }

      try {
        if (formRegisterToMarketplace && returnedTemplate.id && !teamTemplate?.marketplaceId) {
          const response = 
            await dispatch(
              registerTeamTemplateToMarketplace({ 
                session, 
                templateId: returnedTemplate.id 
              })
            ).unwrap();
          const responseTemplate = TeamTemplate.fromJSON(response);
          navigate(`/marketplace/profiles/teams/edit/${responseTemplate.marketplaceId?.value}`);
        }
      }
      catch (error) {
        console.error("Failed to register vendor-team template to marketplace: " + error);
        enqueueSnackbar("Failed to register vendor-team template to marketplace. Please try again.", {
          variant: "error",
        });
      }

      enqueueSnackbar("Saved vendor-team template", { variant: "success" });
      onSave?.(returnedTemplate);
    } catch (error) {
      // 409 Conflict is for duplicate vendor-team names for calling user
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Conflict) {
        enqueueSnackbar(error.response.data, { variant: "warning" });
      } else if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        enqueueSnackbar(error.response.data, { variant: "error" });
      } else {
        console.error("Failed to save vendor-team template: " + error);
        enqueueSnackbar("Failed to save vendor-team template. Please try again.", {
          variant: "error",
        });
      }
    } finally {
      setSaving(false);
    }
  }

  function isCurrentTeamLeader(): boolean {
    return session.user?.id?.isEqualTo(formSelectedTeamLeader?.userId) ?? false;
  }

  return (
    <Wrapper>
      <HeadSection>
        <HeadInputFields>
          <TeamNameField
            value={formTeamName}
            onChange={(event) => {
              setFormTeamName(event.target.value);
              onFormDirty(true);
            }}
            label="Team Name"
            required
          />
          {session.accountType === AccountType.Vendor && (
            <Tooltip
              title={(function () {
                if (!isCurrentTeamLeader())
                  return "Only the vendor-team leader can register the vendor-team on the marketplace";
                if (teamTemplate?.marketplaceId !== undefined) return "Team is already registered on the marketplace";
                return "";
              })()}
            >
              <span>
                <FormControlLabel
                  label="Register Team On Marketplace"
                  labelPlacement={isLargeDownDisplay ? "end" : "start"}
                  control={
                    <Checkbox
                      color="primary"
                      checked={formRegisterToMarketplace}
                      disabled={teamTemplate?.marketplaceId !== undefined || !isCurrentTeamLeader()}
                      onChange={(event) => {
                        onFormDirty(true);
                        setFormRegisterToMarketplace(event.target.checked);
                      }}
                    />
                  }
                />
              </span>
            </Tooltip>
          )}
        </HeadInputFields>
      </HeadSection>
      <TeamRoster
        leader={formSelectedTeamLeader}
        members={formSelectedTeamMembers}
        hideCommentButton={true}
        disableComments={true}
        commentsToolTip={""}
        navigate={navigate}
        onLeaderUpdated={handleLeaderReplaced}
        onMemberAdded={handleMemberAdded}
        onMemberRemoved={handleMemberRemoved}
        onMarketplaceTeamSelected={handleMarketplaceTeamSelected}
      />
      <SaveButton
        color="primary"
        variant="contained"
        disabled={!formValidated || saving}
        size="large"
        loading={saving}
        startIcon={<SaveIcon />}
        onClick={handleSaveTeamClick}
      >
        Save Team
      </SaveButton>
    </Wrapper>
  );
}
